import React, { useCallback, useMemo, useState } from "react";
import { GridRowId, GridRowParams } from "@mui/x-data-grid-pro";
import {
  buildTimestampedExcelFileName,
  RATDataGrid,
  ratDataGridFillSx,
} from "components/RATDataGrid";
import { TableSummary } from "types/tableSummary";
import { useTypedSelector } from "store";
import colors from "theme/colors";
import { getColumns } from "./tableSummaryGrid.columns";
import { TABLE_SUMMARY_GRID_PERSISTENCE } from "./tableSummaryGrid.state";
import { TableSummaryDetailPanel } from "./TableSummaryDetailPanel";

/** Shared tint for expanded summary row (matches kit header / RulesTable). */
const HEADER_BG = colors.tables.columnsAndRowsTitles;
const EXPAND_ROW_BG = HEADER_BG;
/** Detail panel body stays white so the embedded RulesTable reads on paper. */
const EXPAND_PANEL_BG = colors.white;
/** Blue stroke that frames the expanded summary row + detail panel as one card. */
const EXPAND_ACCENT = colors.secondary;
const EXPAND_ACCENT_WIDTH_PX = 2;
const EXPAND_RADIUS_PX = 10;

/**
 * Class toggled from our controlled `detailPanelExpandedRowIds` via
 * `getRowClassName`. Using our own class (not only MUI's
 * `row--detailPanelExpanded`) avoids stale outline chrome when virtualization
 * recycles row DOM nodes on collapse/re-expand.
 */
const EXPANDED_CARD_ROW_CLASS = "tableSummary-expandedCard";

/**
 * Table Summary–only chrome on top of RATDataGrid defaults (`ratDataGridSx`).
 *
 * Goals:
 * - Paint expanded summary + detail panel as one blue-outlined card
 * - Keep the card outline stable under row virtualization (see class above)
 */
const tableSummaryGridSx = {
  // Fill the product-detail panel; row area scrolls, footer stays put.
  ...ratDataGridFillSx,

  // Expanded summary row: tint + rounded top corners (bottom stays open so it
  // visually joins the detail panel beneath). `position: relative` anchors the
  // ::after outline overlay.
  [`& .MuiDataGrid-row.${EXPANDED_CARD_ROW_CLASS}`]: {
    position: "relative",
    backgroundColor: EXPAND_ROW_BG,
    borderTopLeftRadius: EXPAND_RADIUS_PX,
    borderTopRightRadius: EXPAND_RADIUS_PX,
  },

  // Expanded-row cells: drop the row divider under the summary and clear fills
  // so the row tint shows through. `!important` beats kit pinned-cell white and
  // MUI hover/selected `background` shorthand on pinned columns.
  [`& .MuiDataGrid-row.${EXPANDED_CARD_ROW_CLASS} .MuiDataGrid-cell`]: {
    borderBottom: "none",
    background: "transparent !important",
  },

  // Card outline on the summary row (top / left / right only — bottom is open).
  // Drawn as ::after so it sits above sticky pinned cells (z-index 30) that would
  // otherwise cover a normal row border/box-shadow. Right edge is inset by the
  // vertical scrollbar width so the stroke lines up with the detail panel, which
  // MUI already sizes excluding the scrollbar.
  [`& .MuiDataGrid-row.${EXPANDED_CARD_ROW_CLASS}::after`]: {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: "calc(var(--DataGrid-hasScrollY) * var(--DataGrid-scrollbarSize))",
    borderTop: `${EXPAND_ACCENT_WIDTH_PX}px solid ${EXPAND_ACCENT}`,
    borderLeft: `${EXPAND_ACCENT_WIDTH_PX}px solid ${EXPAND_ACCENT}`,
    borderRight: `${EXPAND_ACCENT_WIDTH_PX}px solid ${EXPAND_ACCENT}`,
    borderBottom: "none",
    borderTopLeftRadius: EXPAND_RADIUS_PX,
    borderTopRightRadius: EXPAND_RADIUS_PX,
    // Overlay must not steal clicks from the expand toggle / actions menu.
    pointerEvents: "none",
    zIndex: 50,
  },

  // Virtualization safety net: when our expand class is absent, kill any leftover
  // ::after so collapse does not leave ghost borders on recycled row nodes.
  [`& .MuiDataGrid-row:not(.${EXPANDED_CARD_ROW_CLASS})::after`]: {
    content: "none",
    display: "none",
  },

  // Detail panel under the summary row: completes the card (left / right / bottom
  // borders + bottom radii). No top border — it abuts the summary row. Width already
  // excludes the scrollbar, so edges align with the summary ::after. Margin separates
  // consecutive expanded cards when multiple rows are open.
  "& .MuiDataGrid-detailPanel": {
    backgroundColor: EXPAND_PANEL_BG,
    borderLeft: `${EXPAND_ACCENT_WIDTH_PX}px solid ${EXPAND_ACCENT}`,
    borderRight: `${EXPAND_ACCENT_WIDTH_PX}px solid ${EXPAND_ACCENT}`,
    borderBottom: `${EXPAND_ACCENT_WIDTH_PX}px solid ${EXPAND_ACCENT}`,
    borderBottomLeftRadius: EXPAND_RADIUS_PX,
    borderBottomRightRadius: EXPAND_RADIUS_PX,
    marginBottom: "10px",
    boxSizing: "border-box",
  },
} as const;

export interface TableSummaryGridProps {
  readonly summaries: readonly TableSummary[];
  readonly onReloadData?: () => void;
  readonly isRefreshing?: boolean;
}

export function TableSummaryGrid({
  summaries,
  onReloadData,
  isRefreshing = false,
}: TableSummaryGridProps) {
  const productLineName = useTypedSelector(
    (state) => state.products.selectedProductLine?.name ?? ""
  );
  const columns = useMemo(() => getColumns(), []);
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
    GridRowId[]
  >([]);

  const getDetailPanelContent = useCallback(
    (params: GridRowParams<TableSummary>) => (
      <TableSummaryDetailPanel tableId={params.row.tableId} />
    ),
    []
  );

  const getDetailPanelHeight = useCallback(() => "auto" as const, []);

  const getRowClassName = useCallback(
    (params: GridRowParams<TableSummary>) =>
      detailPanelExpandedRowIds.includes(params.id)
        ? EXPANDED_CARD_ROW_CLASS
        : "",
    [detailPanelExpandedRowIds]
  );

  const handleReloadData = useCallback(() => {
    setDetailPanelExpandedRowIds([]);
    onReloadData?.();
  }, [onReloadData]);

  return (
    <RATDataGrid
      rows={summaries}
      columns={columns}
      getRowId={(row) => row.tableId}
      getRowHeight={() => "auto"}
      getRowClassName={getRowClassName}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      detailPanelExpandedRowIds={detailPanelExpandedRowIds}
      onDetailPanelExpandedRowIdsChange={setDetailPanelExpandedRowIds}
      pagination={false}
      density="compact"
      loading={isRefreshing}
      persistence={TABLE_SUMMARY_GRID_PERSISTENCE}
      toolbar={{
        showAddButton: false,
        showApplySortButton: false,
        showClearSortButton: false,
        showDensityButton: false,
        showQuickFilter: true,
        quickFilterPlaceholder: "Search tables...",
        showFilterButton: true,
        showColumnsButton: true,
        showResetLayout: true,
        showExcelExport: true,
        excelExportConfig: {
          sheetName: "Table Summary",
          getFileName: () =>
            buildTimestampedExcelFileName("Table Summary", productLineName),
        },
        showReloadButton: true,
        onReloadData: handleReloadData,
        isRefreshing,
      }}
      localeText={{ footerTotalRows: "Tables Displayed:" }}
      sx={tableSummaryGridSx}
    />
  );
}
